import React, { useEffect, useRef, useState } from "react";
import { HelpCircle, X } from "lucide-react";

/**
 * Petite icône "?" à placer à côté d'un bouton.
 * Au clic, affiche un popover B&W avec une explication.
 *
 * Usage :
 *   <button>Action</button>
 *   <HelpHint text="Cette action fait XYZ." />
 *
 * Variantes :
 *   - `align` : "start" | "end" | "center" (alignement horizontal du popover)
 *   - `placement` : "top" | "bottom" (au-dessus ou sous l'icône)
 *   - `size` : taille de l'icône (par défaut 14)
 */
export default function HelpHint({ text, title, align = "end", placement = "bottom", size = 14, testid }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  // Fermer au clic extérieur ou ESC
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onEsc = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onEsc);
    };
  }, [open]);

  const alignClass =
    align === "start" ? "left-0" : align === "center" ? "left-1/2 -translate-x-1/2" : "right-0";
  const placementClass = placement === "top" ? "bottom-full mb-2" : "top-full mt-2";

  return (
    <span className="relative inline-flex" ref={ref}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          setOpen((v) => !v);
        }}
        className={`inline-flex items-center justify-center rounded-full transition-colors ${
          open ? "text-[var(--text)]" : "text-[var(--muted-2)] hover:text-[var(--text)]"
        }`}
        aria-label="Aide"
        aria-expanded={open}
        data-testid={testid || "help-hint-btn"}
        style={{ width: size + 8, height: size + 8 }}
      >
        <HelpCircle size={size} strokeWidth={2} />
      </button>

      {open && (
        <span
          role="dialog"
          className={`absolute z-50 ${placementClass} ${alignClass} w-72 rounded-xl p-3 shadow-xl`}
          style={{
            background: "var(--surface)",
            border: "1px solid var(--border-strong)",
            color: "var(--text)",
          }}
          data-testid="help-hint-popover"
          onClick={(e) => e.stopPropagation()}
        >
          {title && (
            <div className="flex items-start justify-between gap-2 mb-1.5">
              <span className="font-semibold text-sm">{title}</span>
              <button
                type="button"
                onClick={() => setOpen(false)}
                className="text-[var(--muted)] hover:text-[var(--text)] transition-colors -mt-0.5"
                aria-label="Fermer"
              >
                <X size={14} strokeWidth={2.2} />
              </button>
            </div>
          )}
          <p className="text-xs leading-relaxed text-[var(--text-2)]">{text}</p>
        </span>
      )}
    </span>
  );
}
